import React, { useState, useEffect } from 'react';
import { CreditCard, Tag, Shield, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import type { StripeCardElement } from '@stripe/stripe-js';

interface BillingInfoFormProps {
  initialData?: {
    signupCode?: string;
    stripePaymentMethodId?: string;
  };
  onSubmit: (data: {
    signupCode?: string;
    stripePaymentMethodId?: string;
  }) => void;
  isLoading: boolean;
}

export default function BillingInfoForm({ 
  initialData, 
  onSubmit, 
  isLoading 
}: BillingInfoFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  
  const [formData, setFormData] = useState({
    signupCode: initialData?.signupCode || '',
    cardholderName: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const [discountInfo, setDiscountInfo] = useState<{
    isValid: boolean;
    description?: string;
    discountValue?: number;
    discountType?: string;
  } | null>(null);

  const [pricing, setPricing] = useState<{
    monthlyPricePerUser: number;
  } | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  const hasFullDiscount = discountInfo?.isValid &&
                         discountInfo?.discountType === 'percentage' &&
                         discountInfo?.discountValue === 100;
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';
  const allowDevBillingBypass = (import.meta.env.DEV || isLocalhost) &&
                                import.meta.env.VITE_ALLOW_BILLING_DEV_BYPASS !== 'false';
  const shouldSkipPaymentDetails = hasFullDiscount || allowDevBillingBypass;


  // Update form data when initialData changes (for form persistence)
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        signupCode: initialData.signupCode || '',
        // Don't restore credit card fields for security
      }));
    }
  }, [initialData]);

  // Fetch current system pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setPricingLoading(true);
        const response = await api.get('/organizations/pricing');
        if (response.data.success) {
          setPricing(response.data.data);
        }
      } catch (error) {
        console.warn('Failed to fetch pricing:', error);
        setPricing(null);
      } finally {
        setPricingLoading(false);
      }
    };
    
    fetchPricing();
  }, []);


  const handleSignupCodeInput = (code: string) => {
    // Simply store the code without API validation
    // Validation will happen on the backend during organization creation
    if (code.trim()) {
      // Show informational message when user enters any code
      setDiscountInfo({
        isValid: true, // We assume it's valid for UI purposes
        description: 'Code entered - validation will occur during setup',
        discountValue: 0, // Don't show any discount amount
        discountType: 'info' // Custom type for informational display
      });
    } else {
      setDiscountInfo(null);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!shouldSkipPaymentDetails) {
      if (!formData.cardholderName.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }

      if (!cardComplete) {
        newErrors.card = 'Please enter complete card information';
      }

      if (cardError) {
        newErrors.card = cardError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // If 100% discount OR local development bypass, skip payment method creation.
    if (shouldSkipPaymentDetails) {
      if (allowDevBillingBypass) {
        console.log('Development billing bypass enabled: skipping Stripe validation');
      }
      onSubmit({
        signupCode: formData.signupCode.trim() || undefined,
        stripePaymentMethodId: undefined,
      });
      return;
    }

    // Create Stripe payment method
    if (!stripe || !elements) {
      setErrors({ card: 'Stripe is not loaded. Please refresh and try again.' });
      return;
    }

    const cardElement = elements.getElement(CardElement) as unknown as StripeCardElement | null;
    if (!cardElement) {
      setErrors({ card: 'Card element not found. Please refresh and try again.' });
      return;
    }

    setProcessingPayment(true);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: formData.cardholderName.trim(),
        },
      });

      if (error) {
        console.error('Stripe payment method creation error:', error);
        setErrors({ card: error.message || 'Failed to process card information' });
        setProcessingPayment(false);
        return;
      }

      if (paymentMethod) {
        console.log('✅ Stripe payment method created:', paymentMethod.id);
        onSubmit({
          signupCode: formData.signupCode.trim() || undefined,
          stripePaymentMethodId: paymentMethod.id,
        });
      }
    } catch (error) {
      console.error('Error creating payment method:', error);
      setErrors({ card: 'Failed to process payment information. Please try again.' });
      setProcessingPayment(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSignupCodeChange = (value: string) => {
    setFormData(prev => ({ ...prev, signupCode: value }));
    if (errors.signupCode) {
      setErrors(prev => ({ ...prev, signupCode: '' }));
    }
    
    // Handle signup code input (no API call needed)
    handleSignupCodeInput(value);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center mx-auto mb-3">
          <CreditCard className="h-6 w-6 text-purple-600" />
        </div>
        <p className="text-muted-foreground">
          Set up your billing information to complete your organization setup.
        </p>
      </div>

      {/* Pricing Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Nexus Pricing</h3>
        {pricingLoading ? (
          <div className="flex items-center gap-2 mb-1">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-700">Loading pricing...</span>
          </div>
        ) : pricing ? (
          <div className="text-2xl font-bold text-blue-900 mb-1">
            ${pricing.monthlyPricePerUser.toFixed(2)} <span className="text-sm font-normal">AUD per user per month</span>
          </div>
        ) : (
          <div className="text-red-600 mb-1">
            <span className="text-sm">Unable to load pricing. Please refresh the page.</span>
          </div>
        )}
        <p className="text-sm text-blue-700">
          Subscription-based pricing per active user
        </p>
      </div>

      {/* Signup Code */}
      <div>
        <label htmlFor="signupCode" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Signup Code (Optional)
        </label>
        <input
          id="signupCode"
          type="text"
          value={formData.signupCode}
          onChange={(e) => handleSignupCodeChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.signupCode ? 'border-red-500' : 'border-input'
          }`}
          placeholder="Enter signup code for discounts"
          disabled={isLoading}
        />
        {errors.signupCode && (
          <p className="text-sm text-red-500 mt-1">{errors.signupCode}</p>
        )}
        
        {/* Discount Code Feedback */}
        {discountInfo && (
          <div className="mt-2 p-3 rounded-md bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-2 text-blue-800">
              <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm">
                  To confirm your code has been correctly applied, go to <span className="font-medium">Site Settings - Billing</span>. Your code and its details will be displayed, and can be changed from there.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Method Section - Hidden if payment details are not required */}
      {!shouldSkipPaymentDetails && (
        <>
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Payment Method
            </h3>
            
            {/* Cardholder Name */}
            <div className="mb-4">
              <label htmlFor="cardholderName" className="block text-sm font-medium mb-1">
                Cardholder Name <span className="text-red-500">*</span>
              </label>
              <input
                id="cardholderName"
                type="text"
                value={formData.cardholderName}
                onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.cardholderName ? 'border-red-500' : 'border-input'
                }`}
                placeholder="Name as it appears on card"
                disabled={isLoading}
              />
              {errors.cardholderName && (
                <p className="text-sm text-red-500 mt-1">{errors.cardholderName}</p>
              )}
            </div>

            {/* Stripe Card Element */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Card Information <span className="text-red-500">*</span>
              </label>
              <div className={`w-full px-3 py-3 border rounded-md focus-within:ring-2 focus-within:ring-primary ${
                errors.card ? 'border-red-500' : 'border-input'
              }`}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                    hidePostalCode: true,
                  }}
                  onChange={(event) => {
                    setCardComplete(event.complete);
                    setCardError(event.error ? event.error.message : null);
                    
                    // Clear errors when user starts typing
                    if (errors.card && event.complete) {
                      setErrors(prev => ({ ...prev, card: '' }));
                    }
                  }}
                />
              </div>
              {errors.card && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.card}
                </p>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-700">
              <Shield className="h-4 w-4" />
              <p className="text-sm">
                Your payment information is securely processed by Stripe. We never store your card details.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Development Mode Indicator */}
      {allowDevBillingBypass && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Development Mode</span>
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            Stripe validation is bypassed. You can proceed without entering payment details.
          </p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isLoading || processingPayment}
          className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processingPayment ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Processing...
            </>
          ) : (
            <>
              {shouldSkipPaymentDetails ? 'Complete Setup' : 'Complete Setup & Subscribe'}
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
